/**
 * Highcharts `tooltip.outside: true` appends `.highcharts-tooltip-container` to
 * `document.body`. Tooltip HTML uses `var(--text-*)` / `var(--background-*)` /
 * `var(--card-shadow-color)` (Highcharts tooltip shadow) which must resolve from `document.documentElement`, not from a chart inside a
 * modal preview that overrides tokens locally.
 *
 * While a modal preview runs, mirror **preview** tokens onto `:root` so portaled
 * tooltips match the preview (light or dark), not the global app theme alone.
 * Capture inline `:root` values once when the modal opens and restore on destroy.
 */

/** Vars read/written on `document.documentElement.style` for portal tooltips. */
// Includes `--card-shadow-color` for Highcharts `tooltip.shadow.color` on portaled HTML tooltips.
export const PORTAL_CHART_TOOLTIP_ROOT_VAR_KEYS = [
	'--text-primary-color',
	'--text-secondary-color',
	'--background-primary-color',
	'--background-color',
	'--border-primary-color',
	'--chart-text-color',
	'--card-shadow-color',
] as const;

export type PortalChartTooltipRootVarKey =
	(typeof PORTAL_CHART_TOOLTIP_ROOT_VAR_KEYS)[number];

/** Matches `.preview-area-outer--dark` in palette-creation-modal.component.scss */
export const PORTAL_CHART_PREVIEW_DARK_INLINE: Readonly<
	Record<PortalChartTooltipRootVarKey, string>
> = {
	'--text-primary-color': 'rgba(255, 255, 255, 0.95)',
	'--text-secondary-color': 'rgba(255, 255, 255, 0.68)',
	'--background-primary-color': '#0f1526',
	'--background-color': '#0f1526',
	'--border-primary-color': 'rgba(255, 255, 255, 0.16)',
	'--chart-text-color': 'rgba(255, 255, 255, 0.95)',
	'--card-shadow-color': '#0000004d',
};

/** Matches `.jepto-palette-creation-modal` host light chrome (preview “Light”). */
export const PORTAL_CHART_PREVIEW_LIGHT_INLINE: Readonly<
	Record<PortalChartTooltipRootVarKey, string>
> = {
	'--text-primary-color': 'rgba(15, 21, 38, 0.87)',
	'--text-secondary-color': 'rgba(15, 21, 38, 0.54)',
	'--background-primary-color': '#ffffff',
	'--background-color': '#ffffff',
	'--border-primary-color': 'rgba(0, 0, 0, 0.12)',
	'--chart-text-color': 'rgba(15, 21, 38, 0.87)',
	// Default light theme card shadow (theme.defaults); softer than typical :root app card shadow.
	'--card-shadow-color': '#00000026',
};

export type PortalChartTooltipRootCapture = Partial<
	Record<PortalChartTooltipRootVarKey, string>
>;

/** Snapshot current *inline* declarations on `root` (e.g. ThemeService on `:root`). */
export function capturePortalChartTooltipRootVars(
	root: HTMLElement
): PortalChartTooltipRootCapture {
	const out: PortalChartTooltipRootCapture = {};
	for (const key of PORTAL_CHART_TOOLTIP_ROOT_VAR_KEYS) {
		out[key] = root.style.getPropertyValue(key);
	}
	return out;
}

export function applyPortalChartPreviewDarkTooltipVars(
	root: HTMLElement
): void {
	for (const key of PORTAL_CHART_TOOLTIP_ROOT_VAR_KEYS) {
		root.style.setProperty(key, PORTAL_CHART_PREVIEW_DARK_INLINE[key]);
	}
}

export function applyPortalChartPreviewLightTooltipVars(
	root: HTMLElement
): void {
	for (const key of PORTAL_CHART_TOOLTIP_ROOT_VAR_KEYS) {
		root.style.setProperty(key, PORTAL_CHART_PREVIEW_LIGHT_INLINE[key]);
	}
}

export function restorePortalChartTooltipRootVars(
	root: HTMLElement,
	capture: PortalChartTooltipRootCapture
): void {
	for (const key of PORTAL_CHART_TOOLTIP_ROOT_VAR_KEYS) {
		const v = capture[key]?.trim();
		if (v) {
			root.style.setProperty(key, v);
		} else {
			root.style.removeProperty(key);
		}
	}
}
